// Cliente de la edición de video con IA (Gemini Omni Flash).
//
// Feature NUEVA e independiente del editor de Sora (`editar_vid`): NO lo
// reemplaza. Flujo async en 2 pasos (mismo patrón que traducción/borrado):
//   1) START → dispara el job y devuelve un jobId (n8n corre Gemini en
//      background y sube el mp4 editado a `video-project-assets`).
//   2) STATUS → se polea hasta `done` (con el storage path) o `error`.
//
// Solo para clips ≤10s (límite del modelo). Ver ADR-500 y
// docs/2026-07-07-gemini-omni-editar-clips-timeline.md.
import { supabase } from './supabase';
import {
  GENERATE_AUTH_TOKEN,
  OMNI_EDIT_START_URL,
  OMNI_EDIT_STATUS_URL,
  USE_ATLAS_BACKEND,
} from '../config';
import { atlasBackendFetch } from './atlasBackend';

export type OmniAspectRatio = '16:9' | '9:16' | '1:1' | '4:5';
export type OmniEditStatus = 'processing' | 'done' | 'error';

export interface OmniEditParams {
  userId: string;
  companyId: string;
  projectId: string;
  /** Signed URL (descargable) del clip a editar. */
  clipUrl: string;
  instruction: string;
  aspectRatio: OmniAspectRatio;
  /**
   * Segundos de ARCHIVO que cubre el tramo a editar (con la velocidad ya
   * aplicada: `segundosDelRecorteOmni`). Define el costo: 1 cr/s.
   */
  durationSec: number;
  /**
   * Dónde empieza el tramo dentro del archivo, en segundos. Presente = el
   * worker corta ese tramo y le manda a Gemini SÓLO eso (`media_offset_sec`);
   * ausente = va el archivo entero (historial, que no tiene timeline). Un 0
   * también viaja: es "empieza al principio", no "no hay tramo".
   * Ver ADR-2026-09-23-omni-edit-el-worker-recorta-la-ventana.
   */
  mediaOffsetSec?: number;
  /**
   * Signed URL de una imagen de referencia OPCIONAL (ej. una foto más clara del
   * producto) para guiar la edición. Si no hay, se manda vacío y el flujo n8n
   * cae en el camino "solo texto" (video + prompt) — idéntico al comportamiento
   * previo. Ver ADR y `omni_edit_start` (nodo IF "¿hay imagen?").
   */
  referenceImageUrl?: string;
  /** Etiqueta para el panel (ADR-604): 'estudio_edicion' (default, timeline
   *  del estudio) o 'editar_video_omni' (desde el historial). */
  modoUtilizado?: string;
}

export interface OmniEditResult {
  jobId: string;
  /** Storage path del video editado dentro del bucket `video-project-assets`. */
  path: string;
  /** Signed URL lista para reproducir el resultado. */
  signedUrl: string;
}

const BUCKET = 'video-project-assets';
const POLL_INTERVAL_MS = 3000;
// ~35 min: Omni tarda ~90 s, pero si falla o se cuelga (hasta 5 min) el backend
// reintenta con Seedance 2.5, cuya cola puede tardar hasta 30 min. Cortar antes
// dejaba la edición en error mientras el respaldo seguía y se cobraba
// (ADR-2026-09-27-editar-clip-con-seedance-2-5-de-respaldo).
const POLL_MAX_ATTEMPTS = 700;

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

/**
 * Costo en créditos de una edición: 1 crédito por segundo, mínimo 1.
 * Espejo de la tarifa del backend (`calcular gasto de creditos` en n8n).
 */
export function creditsForOmniEdit(durationSec: number): number {
  return Math.max(1, Math.ceil(durationSec));
}

/** Tope de duración del modelo. Gemini rebota con 400 si se pasa. */
export const OMNI_MAX_DURATION_SEC = 10;

/**
 * Segundos de ARCHIVO que cubre el tramo del clip que está en la timeline —
 * lo que el worker corta y le manda a Gemini, y lo que se cobra.
 *
 * `duration` es tiempo de timeline; a `speed` 2x esos segundos consumen el
 * doble de material, a 0.5x la mitad. Y el tramo no puede pasar el final del
 * archivo (`sourceDuration - mediaOffset`) aunque la timeline crea otra cosa.
 * Ver ADR-2026-09-23-omni-edit-el-worker-recorta-la-ventana (supersede el tope
 * por fuente entera: el backend ya no procesa el archivo completo).
 */
export function segundosDelRecorteOmni(clip: {
  duration: number;
  speed?: number;
  mediaOffset?: number;
  sourceDuration?: number;
}): number {
  const velocidad =
    typeof clip.speed === 'number' && Number.isFinite(clip.speed) && clip.speed > 0
      ? clip.speed
      : 1;
  const material = clip.duration * velocidad;
  const fuente = clip.sourceDuration;
  if (typeof fuente === 'number' && Number.isFinite(fuente) && fuente > 0) {
    const offset =
      typeof clip.mediaOffset === 'number' && Number.isFinite(clip.mediaOffset)
        ? Math.max(0, clip.mediaOffset)
        : 0;
    return Math.max(0, Math.min(material, fuente - offset));
  }
  return material;
}

/** `true` si el tramo del clip no entra en el tope del modelo. */
export function excedeElTopeDeOmni(clip: {
  duration: number;
  speed?: number;
  mediaOffset?: number;
  sourceDuration?: number;
}): boolean {
  return segundosDelRecorteOmni(clip) > OMNI_MAX_DURATION_SEC;
}

/**
 * Con qué URL se manda el pedido de edición.
 *
 * `firmar` vuelve a firmar el archivo con validez larga. Si devuelve una URL,
 * va esa. Si falla (devuelve null o tira), devolvemos **null**: el pedido NO se
 * manda. Mandar la URL vieja "por las dudas" es justo lo que produce el
 * `no se pudo descargar el video (400)` que este flujo arregla — el worker la
 * usa 15-27 min después y la encuentra vencida, así que el usuario espera un
 * minuto para ver un error del proveedor en vez de uno claro al instante.
 * Hallazgo de Greptile en el PR #131, ver ADR-2026-09-23-omni-edit-no-manda-url-vieja.
 *
 * Cuando NO hay con qué firmar (`firmar` nulo: el contenedor no sabe el bucket
 * o el clip no tiene `storagePath`) se manda `urlActual`, que es lo único que
 * hay — cortar ahí sería romper un caso que hoy funciona.
 */
export async function urlParaMandarOmni(p: {
  urlActual: string;
  firmar?: (() => Promise<string | null>) | null;
}): Promise<string | null> {
  if (!p.firmar) return p.urlActual;
  try {
    return (await p.firmar()) ?? null;
  } catch (e) {
    console.warn('[omni-edit] no pude refirmar la URL del clip:', e);
    return null;
  }
}

/**
 * Body del start. Separado de la llamada para poder testear el contrato: qué
 * campos viajan y cuándo. `media_offset_sec` sólo va si el llamador lo pasó —
 * su ausencia es lo que le dice al backend "archivo entero".
 */
export function armarBodyDeOmniStart(params: OmniEditParams, localJobId: string) {
  return {
    user_id: params.userId,
    company_id: params.companyId,
    project_id: params.projectId,
    job_id: localJobId,
    clip_url: params.clipUrl,
    instruction: params.instruction,
    aspect_ratio: params.aspectRatio,
    duration_sec: params.durationSec,
    ...(typeof params.mediaOffsetSec === 'number' && Number.isFinite(params.mediaOffsetSec)
      ? { media_offset_sec: Math.max(0, params.mediaOffsetSec) }
      : {}),
    // Vacío si el usuario no adjuntó imagen → el backend usa el camino "solo texto".
    reference_image_url: params.referenceImageUrl ?? '',
    // Trackeo (ADR-585/604): superficie desde la que se editó.
    modo_utilizado: params.modoUtilizado ?? 'estudio_edicion',
  };
}

/**
 * Dispara la edición y polea el status hasta que termina. Devuelve el path +
 * signed URL del video editado. Lanza Error si falla o si hace timeout.
 * `onStatus` reporta las transiciones (para la UI).
 */
export async function omniEditVideo(
  params: OmniEditParams,
  onStatus?: (s: OmniEditStatus) => void,
): Promise<OmniEditResult> {
  const jobId = await iniciarEdicionOmni(params);
  onStatus?.('processing');
  return esperarEdicionOmni(jobId, onStatus);
}

/**
 * Sólo el START: devuelve el id del trabajo. Separado de la espera para que la
 * timeline del Estudio lo guarde en el clip y pueda RETOMAR la espera después de
 * recargar (ADR-2026-09-27-editar-clip-sigue-con-el-panel-cerrado).
 */
export async function iniciarEdicionOmni(params: OmniEditParams): Promise<string> {
  // job_id propio: lo usa el fallback n8n; el atlas lo ignora y devuelve el
  // suyo en la respuesta del start (ADR-601) — polleamos con ese.
  const localJobId = crypto.randomUUID();
  const startBody = armarBodyDeOmniStart(params, localJobId);

  // ── 1) START ──────────────────────────────────────────────────────────
  let startRes: Response;
  if (USE_ATLAS_BACKEND) {
    const { user_id: _u, company_id: _c, ...atlasBody } = startBody;
    startRes = await atlasBackendFetch('/v1/videos/omni-edit/start', atlasBody);
  } else {
    startRes = await fetch(OMNI_EDIT_START_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(startBody),
    });
  }
  if (!startRes.ok) {
    throw new Error(`Error al iniciar la edición (${startRes.status}).`);
  }
  const startRaw = await startRes.text();
  let startJson: { response?: string; jobId?: string; error?: string };
  try {
    startJson = JSON.parse(startRaw);
  } catch {
    throw new Error('Respuesta inválida del servidor de edición.');
  }
  if (startJson.response !== 'success') {
    throw new Error(startJson.error || 'No se pudo iniciar la edición.');
  }
  // Atlas: jobId del backend; n8n: el nuestro.
  return startJson.jobId || localJobId;
}

/**
 * Polea el status de un trabajo ya iniciado hasta que termina: el video editado o
 * un Error. Sirve igual para uno recién disparado que para uno retomado.
 */
export async function esperarEdicionOmni(
  jobId: string,
  onStatus?: (s: OmniEditStatus) => void,
): Promise<OmniEditResult> {
  // ── 2) POLL STATUS ────────────────────────────────────────────────────
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);
    let st: { status?: string; path?: string; error?: string };
    try {
      const stRes = USE_ATLAS_BACKEND
        ? await atlasBackendFetch('/v1/videos/omni-edit/status', {
            job_id: jobId,
          })
        : await fetch(OMNI_EDIT_STATUS_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: GENERATE_AUTH_TOKEN,
            },
            body: JSON.stringify({ job_id: jobId }),
          });
      if (!stRes.ok) continue; // reintentar el poll
      st = await stRes.json();
    } catch {
      continue; // error de red puntual → reintentar
    }

    if (st.status === 'done' && st.path) {
      const { data: urlData } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(st.path, 3600);
      if (!urlData?.signedUrl) {
        throw new Error('Video editado pero no se pudo obtener la URL.');
      }
      onStatus?.('done');
      return { jobId, path: st.path, signedUrl: urlData.signedUrl };
    }
    if (st.status === 'error') {
      onStatus?.('error');
      throw new Error(st.error || 'La edición del video falló.');
    }
    // status === 'processing' → seguir poleando
  }

  throw new Error('La edición del video tardó demasiado. Reintentá.');
}
